import React, { useState, useEffect } from "react";
import { ComponentI, LessonI, PageI } from "../../../../types";
import Card from "../../../../componentLibrary/Card";
import Combobox from "../../../../componentLibrary/Combobox";
import { useGetTopics, useGetLesson } from "../../../../api/queries";
import Spinner from "../../../../componentLibrary/Spinner";
import Input from "../../../../componentLibrary/Input";

export default function LessonLinkContentCard({
  component,
  handleContentChange,
  provided,
}: {
  component: ComponentI;
  handleContentChange: (id: string, field: string, value: string) => void;
  provided: any; //eslint-disable-line @typescript-eslint/no-explicit-any
}) {
  const [lessonOptions, setLessonOptions] = useState<LessonI[]>([]);
  const [pageOptions, setPageOptions] = useState<PageI[]>([]);

  const {
    data: modules,
    isError: isErrorModules,
    isLoading: isLoadingModules,
  } = useGetTopics();

  const { data: lesson } = useGetLesson(component.content.lesson_id);

  useEffect(() => {
    if (lesson) {
      setPageOptions(lesson.pages);
    } else {
      setPageOptions([]);
    }
    // If selected page_id doesn't exist in lesson pages, default to first page
    if (
      lesson &&
      !lesson.pages.find((page) => page._id === component.content.page_id)
    ) {
      handleContentChange(component._id, "page_id", lesson.pages[0]._id);
    }
  }, [lesson, component.content.page_id]);

  function getTopicComboboxValue(module_id: string) {
    const moduleObj = modules.find((module) => module._id === module_id);
    if (moduleObj) {
      return { value: moduleObj._id, label: moduleObj.title };
    }
    return { value: "", label: "" };
  }

  function getLessonComboboxValue(module_id: string, lesson_id: string) {
    const moduleObj = modules.find((module) => module._id === module_id);
    if (moduleObj) {
      const lessonObj = moduleObj.lessons.find(
        (lesson) => lesson._id === lesson_id,
      );
      if (lessonObj) {
        return { value: lessonObj._id, label: lessonObj.title };
      }
    }
    return { label: "", value: "" };
  }

  function getPageComboboxValue(page_id: string) {
    const pageObj = pageOptions.find((page) => page._id === page_id);
    if (pageObj) {
      return { value: pageObj._id, label: pageObj.title };
    }
    return { label: "", value: "" };
  }

  useEffect(() => {
    // Setting Initial Values
    if (modules && component.content.module_id !== "") {
      const moduleObj = modules.find(
        (module) => module._id === component.content.module_id,
      );
      if (moduleObj) {
        setLessonOptions(moduleObj.lessons);
      }
    } else {
      setLessonOptions([]);
      setPageOptions([]);
    }
  }, [modules, component.content]);

  if (isLoadingModules) return <Spinner className="mx-auto" />;
  if (isErrorModules || !modules) return <div>Error</div>;

  return (
    <Card>
      <div
        className=""
        data-rbd-drag-handle-context-id={
          provided.dragHandleProps?.["data-rbd-drag-handle-context-id"]
        }
        data-rbd-drag-handle-draggable-id="gibberish"
      >
        <div className="mb-2">
          <Combobox
            placeholder="Select a Module"
            options={modules.map((module) => ({
              label: module.title,
              value: module._id,
            }))}
            onChange={(option) => {
              if (option) {
                handleContentChange(component._id, "module_id", option.value);
              } else {
                handleContentChange(component._id, "module_id", "");
              }
              handleContentChange(component._id, "lesson_id", "");
              handleContentChange(component._id, "page_id", "");
            }}
            value={getTopicComboboxValue(component.content.module_id)}
            disabled={false}
          />
        </div>
        <div className="mb-2">
          <Combobox
            placeholder="Select a Lesson"
            options={lessonOptions.map((lesson) => ({
              label: lesson.title,
              value: lesson._id,
            }))}
            onChange={(option) => {
              if (option) {
                handleContentChange(component._id, "lesson_id", option.value);
              } else {
                handleContentChange(component._id, "lesson_id", "");
                handleContentChange(component._id, "page_id", "");
              }
            }}
            value={getLessonComboboxValue(
              component.content.module_id,
              component.content.lesson_id,
            )}
            disabled={lessonOptions.length === 0}
          />
        </div>
        <div className="mb-2">
          <Combobox
            placeholder="Select a Page"
            options={pageOptions.map((page) => ({
              label: page.title,
              value: page._id,
            }))}
            onChange={(option) => {
              if (option) {
                handleContentChange(component._id, "page_id", option.value);
              } else {
                handleContentChange(component._id, "page_id", "");
              }
            }}
            value={getPageComboboxValue(component.content.page_id)}
            disabled={pageOptions.length === 0}
          />
        </div>
        <Input
          value={component.content.label}
          onChange={(e) =>
            handleContentChange(component._id, "label", e.target.value)
          }
          label="Label"
          placeholder="Enter Label"
        />
      </div>
    </Card>
  );
}
